import React, { Component } from 'react';
import { ReduxState } from '../../store/state';
import { connect } from 'react-redux';
import {
  Flex, Select, Text, Button, Tbody, Tr, Table, Td, Th, Box, Card, CardBody
} from '@chakra-ui/react';
import { MdEdit, MdLocalShipping, MdTimer } from 'react-icons/md';
import { asyncCartOrdersCreate } from '../../store/cart/initiators';
import AppButton from '../global/forms/AppButton';
import { formatCurrency } from '../global/data/formatCurrencyHelper';
import { updateOrdersFlow } from '../../store/cart/actions';
import { colors } from '../../assets/colors';
import { User } from '../../store/user/types';
import { Location as ILocation } from '../../store/location/types';
import moment from 'moment';
import { CartItem } from '../../store/cart/types';
import { PageOrderFlow } from '../../store/page/types';

interface ChoosePaymentFormProps {
  user: User | null;
  selectedLocation: ILocation | null;
  orderFlows: PageOrderFlow[]
  groupedCartItems?: Record<string, CartItem[]> 
  isCalculating: boolean;
  goBack: () => void;
  goForward: () => void;
  updateOrdersFlow: (flows: PageOrderFlow[]) => void;
  asyncCartOrdersCreate: () => void;
}

interface OrderFlowAndCart {
  sourceId: string;
  orderFlow: PageOrderFlow;
  cartItems: CartItem[];
  allowedDeliveryTypes: ('local' | 'interbranch' | 'out of zone' | 'out of range')[];
  allowedPaymentMethods: string[];
  canDeliver: boolean;
}

class ChoosePaymentForm extends Component<ChoosePaymentFormProps> {
  state = {
    paymentMethodFormOpen: false,
    selectedSourceOrder: null
  }

  handleOrderFlowPaymentChange = (sourceId: string, orderFlow: PageOrderFlow, paymentMethod: string) => {
    let updatedOrderFlow = {
      ...orderFlow,
      data: {
        ...orderFlow.data,
        paymentMethods: [{
          name: paymentMethod,
          amount: orderFlow.orderRevenue
        }]
      }
    };
    let updatedOrderFlowList = this.props.orderFlows.map(orderFlow => {
      if (orderFlow.sourceId === sourceId) {
        return updatedOrderFlow
      } else {
        return orderFlow
      }
    })
    this.props.updateOrdersFlow(updatedOrderFlowList)
  }

  renderPaymentMethodForm = (orderFlowAndCart: OrderFlowAndCart) => {
    const { orderFlow, canDeliver, sourceId, allowedPaymentMethods } = orderFlowAndCart;
    const { paymentMethodFormOpen, selectedSourceOrder } = this.state;
    const methodMap: Record<string, string> = {
      'bank transfer bns': 'Bank Transfer - BNS',
      'bank transfer cibc': 'Bank Transfer - CIBC FC',
      'bank transfer ncb': 'Bank Transfer - NCB',
      'cash on delivery': 'Cash on Delivery',
      'card online fygaro': 'Credit/Debit Card Online',
      'credit card on delivery': 'Credit Card on Delivery',
      'debit card on delivery': 'Debit Card on Delivery',
      'lynk': 'Lynk'
    }  
    if (canDeliver && allowedPaymentMethods.includes('card online fygaro') && !(orderFlow.data.paymentMethods.length)) {
      // Initialize it to default to online payments
      this.handleOrderFlowPaymentChange(sourceId, orderFlow, 'card online fygaro')
    }
    if (!canDeliver) {
      return (
        <Tr>
          <Td colSpan={2}>
            <Text>Sorry, the seller has not permitted {orderFlow.deliveryType} deliveries for some items in this order.</Text>
          </Td>
        </Tr>  
      )
    } else if (paymentMethodFormOpen && selectedSourceOrder === sourceId) {
      return (
        <Tr>
          <Td colSpan={2}>
          <Select 
            placeholder='Select a payment method'
            onChange={(e) => {
              this.handleOrderFlowPaymentChange(sourceId, orderFlow, e.target.value)
              this.setState({ paymentMethodFormOpen: false})
            }}
            value={orderFlow.data.paymentMethods.length ? orderFlow.data.paymentMethods[0].name : ''} 
            style={{ borderRadius: 25}}
          >
            {allowedPaymentMethods.map(method => {
              let methodLabel = methodMap[method]
              return <option value={method} key={method}>{methodLabel}</option>
            })}
          </Select>                  
          </Td>
        </Tr>        
      );
    } else if (selectedSourceOrder !== sourceId || !paymentMethodFormOpen) {
      return (
        <Tr>
        <Td colSpan={2} align='center' justifyContent={'center'}>
          <Flex flexDirection={'row'} align={'center'}>
            <Button size={'sm'} onClick={() => this.setState({ paymentMethodFormOpen: true, selectedSourceOrder: sourceId})} mr={2}>
              {
                orderFlow.data.paymentMethods.length ? 
                methodMap[orderFlow.data.paymentMethods[0].name] : 
                'Select a payment method'
              }
            </Button>
            <MdEdit onClick={() => this.setState({ paymentMethodFormOpen: true, selectedSourceOrder: sourceId})}/>
          </Flex>
        </Td>
      </Tr>        
      )
    }
  }
  renderOrderFlow = (orderFlowAndCart: OrderFlowAndCart) => {
    const { isCalculating, user, selectedLocation } = this.props;
    const { orderFlow, canDeliver } = orderFlowAndCart;
    if (!isCalculating) {
      return (
        <Card borderRadius={20} mb={2}>
          <CardBody padding={4}>
            <Table>
              <Tbody>
                <Tr>
                  <Td colSpan={2}>
                    <Flex flexDirection={'row'} align={'center'}>
                      <MdLocalShipping color={canDeliver ? undefined : '#CA0000'}/>
                      <Text ml={2} color={canDeliver ? undefined : '#CA0000'}>{`${user?.firstName} ${user?.lastName} @ ${selectedLocation?.address}`}</Text>
                    </Flex>
                  </Td>            
                </Tr>
                <Tr>
                  <Td colSpan={2}>
                    <Flex flexDirection={'row'} align={'center'}>
                      <MdTimer />
                      <Text ml={2}>{`From Seller: Ready by ${moment(orderFlow.targetDeliveryTime).format('LLL')}`}</Text>
                    </Flex>
                  </Td>                
                </Tr>
                { orderFlow.data.orderItems.map(item => {
                      return (
                        <Tr key={item._id}>
                          <Th>
                            <Flex flexDirection={"row"} align={'center'}>
                              { !item._id.includes('discount') && (
                                <Button borderRadius={'full'} mr={2} size={'sm'}>{item.quantity}</Button>
                              )}
                              <Text>{ item.name }</Text>
                            </Flex>
                          </Th>
                          <Td textAlign={'right'}>
                            { formatCurrency(item.currency, item.price * item.quantity) }
                          </Td>
                        </Tr>
                      )
                    })}
                  <Tr>
                    <Th><Button size={'xs'} mr={2}>{orderFlow.deliveryType.toUpperCase()}</Button>Delivery</Th>
                    <Td textAlign={'right'}>{ formatCurrency(orderFlow.currency, orderFlow.deliveryFee) }</Td>
                  </Tr>
                  <Tr>
                    <Th>Total</Th>
                    <Td textAlign={'right'} fontWeight={'bold'}>{ formatCurrency(orderFlow.currency, orderFlow.orderRevenue) }</Td>
                  </Tr>
                  {this.renderPaymentMethodForm(orderFlowAndCart)}
              </Tbody>
            </Table>
          </CardBody>
        </Card>
    )}

  }

  render = () => {
    const { orderFlows, groupedCartItems, isCalculating } = this.props;
    const orderFlowsAndCarts = orderFlows.map(orderFlow => {
      let sourceId = orderFlow.sourceId || '';
      let cartItems: CartItem[] = [];
      if (groupedCartItems) {
        cartItems = groupedCartItems[sourceId] || [];
      }
      let allowedDeliveryTypesSet: Set<'local' | 'interbranch' | 'out of zone' | 'out of range'> = new Set();
      let allowedPaymentMethodsSet: Set<string> = new Set();
      cartItems.forEach(cartItem => {
        if (cartItem.reference?.pageData?.allowedDeliveryTypes) {
          cartItem.reference.pageData.allowedDeliveryTypes.forEach(type => {
            allowedDeliveryTypesSet.add(type);
          });
        }
        if (cartItem.reference?.pageData?.allowedPaymentMethods) {
          cartItem.reference.pageData.allowedPaymentMethods.forEach(type => {
            allowedPaymentMethodsSet.add(type);
          });
        }
      });      
      const allowedDeliveryTypes: ('local' | 'interbranch' | 'out of zone' | 'out of range')[] = Array.from(allowedDeliveryTypesSet);
      const allowedPaymentMethods: string[] = Array.from(allowedPaymentMethodsSet);
      let canDeliver = false;
      if (allowedDeliveryTypes.includes(orderFlow.deliveryType)) {
        canDeliver = true;
      }
      return ({
      sourceId: sourceId,
      orderFlow,
      cartItems,
      allowedDeliveryTypes,
      allowedPaymentMethods,
      canDeliver
    })})
    
    const hasUndeliverableOrder = orderFlowsAndCarts.some(obj => obj.canDeliver === false);
    const hasUnselectedMethod = orderFlowsAndCarts.some(obj => !obj.orderFlow.data.paymentMethods.length)
    return (
      <Card borderRadius={20}>
        <CardBody padding={4}>
          {!isCalculating && orderFlowsAndCarts.length > 1 && (
            <Text textAlign={'center'} fontWeight={'bold'}>Your items will be delivered as {orderFlowsAndCarts.length} orders</Text>
          )}
          { orderFlowsAndCarts.map((orderFlowAndCart, index) => {
            return (
              <div key={index}>
                {this.renderOrderFlow(orderFlowAndCart)}
              </div>
            )
          })}
          <Box maxW={'xl'}>
          <AppButton text={'Back'} onClick={() => this.props.goBack()} color={colors.shade1}/>
          <AppButton 
            text={ isCalculating ? 'Calculating...' : 'Checkout'}
            isDisabled={isCalculating || hasUndeliverableOrder || hasUnselectedMethod}
            onClick={() =>{ 
              this.props.goForward()
              }}
              
          />
          </Box>
        </CardBody>
      </Card>
    )
  }
}

const mapStateToProps = (state: ReduxState) => {
  const { user } = state.user;
  const { selectedLocation } = state.location;
  const { orderFlows, isCalculating } = state.cart;
  return { isCalculating, user, selectedLocation, orderFlows };
};

export default connect(mapStateToProps, {
  updateOrdersFlow,
  asyncCartOrdersCreate
})(ChoosePaymentForm);